import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    Search,
    MoreHorizontal,
    Eye,
    History,
    Mail,
    UsersRound,
    ChevronsLeft,
    ChevronLeft,
    ChevronRight,
    ChevronsRight,
    ArrowUpDown,
    UserPlus,
    Trash2,
    X,
} from "lucide-react";
import { toast } from "sonner";

import { EmptyState } from "@/components/feedback/EmptyState.tsx";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { TableSkeleton } from "@/components/loading/TableSkeleton.tsx";
import { PageBreadcrumb } from "@/components/layout/PageBreadcrumb.tsx";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
    fetchCustomers,
    fetchCustomerById,
    fetchOrders,
    enrichOrdersWithCustomers,
    getOrders,
    submitCustomer,
    deleteCustomer,
    type ClientRecord,
} from "@/lib/utils";
import { getAuthenticatedUser } from "@/lib/auth";

// ----------------------------------------------------------------------------

interface ClientiProps {
    className?: string;
}

export function Clienti(props: ClientiProps) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { className } = props;

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const [clients, setClients] = useState<ClientRecord[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [isLoadingClients, setIsLoadingClients] = useState(true);
    const [clientsError, setClientsError] = useState<string | null>(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Client creation modal state
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [newFullName, setNewFullName] = useState("");
    const [newEmail, setNewEmail] = useState("");
    const [newPhone, setNewPhone] = useState("");
    const [newAddress, setNewAddress] = useState("");
    const [newCity, setNewCity] = useState("");
    const [newZip, setNewZip] = useState("");
    const [newProvince, setNewProvince] = useState("");
    const [newTaxNumber, setNewTaxNumber] = useState("");
    const [newNotes, setNewNotes] = useState("");
    const [newCode, setNewCode] = useState("");
    const [newCountry, setNewCountry] = useState("");
    const [newTags, setNewTags] = useState("");
    const [isSubmittingCustomer, setIsSubmittingCustomer] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const deletedClientName = location.state?.deletedClientName;

    const currentUser = useMemo(() => getAuthenticatedUser(), []);
    const canViewAll = Boolean(
        currentUser?.canViewAll ||
        currentUser?.id?.toLowerCase() === "dev-nogravity",
    );
    const agentTag = currentUser?.agentTag;
    const agentCode = currentUser?.agentCode;
    const activeAgentTag = canViewAll
        ? ""
        : agentTag || (agentCode ? `agente-${agentCode}` : "none");

    const filteredClients = useMemo(
        () =>
            clients.filter((client) => {
                // Exclude accounts marked as agents from the customer list
                if (client.isAgent === true) {
                    return false;
                }

                // Exclude the currently authenticated agent's own personal profile
                if (
                    currentUser?.id &&
                    client.id.toLowerCase() === currentUser.id.toLowerCase()
                ) {
                    return false;
                }
                if (
                    currentUser?.email &&
                    client.email.toLowerCase() ===
                        currentUser.email.toLowerCase()
                ) {
                    return false;
                }

                // If admin/all-access, allow all clients
                if (canViewAll) {
                    // Allowed
                } else if (agentTag || agentCode) {
                    const clientTags = (client.tags ?? []).map((t) =>
                        t.toLowerCase().trim(),
                    );
                    const targetTag = agentTag
                        ? agentTag.toLowerCase().trim()
                        : "";
                    const targetCode = agentCode ? agentCode.trim() : "";

                    const hasMatchingTag = clientTags.some((t) => {
                        if (targetTag && t === targetTag) return true;
                        if (
                            targetCode &&
                            (t === `agente-${targetCode}` ||
                                t === `agent-${targetCode}`)
                        )
                            return true;
                        return false;
                    });

                    if (!hasMatchingTag) {
                        return false;
                    }
                } else {
                    return false;
                }

                const normalizedQuery = searchQuery.trim().toLowerCase();
                const matchesSearch =
                    normalizedQuery.length === 0 ||
                    client.name.toLowerCase().includes(normalizedQuery) ||
                    client.email.toLowerCase().includes(normalizedQuery) ||
                    (client.city &&
                        client.city.toLowerCase().includes(normalizedQuery)) ||
                    (client.phone &&
                        client.phone.toLowerCase().includes(normalizedQuery)) ||
                    (client.code &&
                        client.code.toLowerCase().includes(normalizedQuery)) ||
                    (client.postalCode &&
                        client.postalCode
                            .toLowerCase()
                            .includes(normalizedQuery));

                return matchesSearch;
            }),
        [clients, searchQuery, canViewAll, agentTag, agentCode, currentUser],
    );

    const totalPages = Math.max(
        1,
        Math.ceil(filteredClients.length / pageSize),
    );
    const paginatedClients = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredClients.slice(start, start + pageSize);
    }, [filteredClients, currentPage, pageSize]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    // -------------------------------------
    // Effects
    // -------------------------------------

    const loadCustomers = React.useCallback(
        async (bypassCache = false) => {
            setIsLoadingClients(true);
            setClientsError(null);

            try {
                const [apiClients, apiOrders] = await Promise.all([
                    fetchCustomers(activeAgentTag),
                    fetchOrders(undefined, activeAgentTag).catch((err) => {
                        console.warn(
                            "[Clienti] Failed to fetch orders for client metrics:",
                            err,
                        );
                        return [];
                    }),
                ]);

                const isStoredenOrderId = (id: string) =>
                    /^\d+_[a-z0-9]+$/i.test(id) || /^\d{4,}$/.test(id);

                const isOrderCancelled = (status: unknown) => {
                    const s = Number(status);
                    if (s === 6 || s === 7) return true;
                    if (typeof status === "string") {
                        const lower = status.toLowerCase().trim();
                        if (
                            lower === "cancelled" ||
                            lower === "annullato" ||
                            lower === "aborted" ||
                            lower === "archived"
                        )
                            return true;
                    }
                    return false;
                };

                const localOrders = getOrders();
                const apiOrderIds = new Set(apiOrders.map((order) => order.id));
                const localOnlyOrders = localOrders.filter((order) => {
                    if (isOrderCancelled(order.status)) return false;
                    return (
                        !isStoredenOrderId(order.id) &&
                        !apiOrderIds.has(order.id)
                    );
                });

                const activeApiOrders = apiOrders.filter(
                    (order) => !isOrderCancelled(order.status),
                );
                const mergedOrders = [...localOnlyOrders, ...activeApiOrders];
                const allOrders = enrichOrdersWithCustomers(
                    mergedOrders,
                    apiClients,
                );

                const enrichedClients = await Promise.all(
                    apiClients.map(async (c) => {
                        const clientOrders = allOrders.filter(
                            (o) =>
                                (o.clientId && o.clientId === c.id) ||
                                (o.email &&
                                    c.email &&
                                    o.email.toLowerCase().trim() ===
                                        c.email.toLowerCase().trim()) ||
                                (o.customerName &&
                                    c.name &&
                                    o.customerName.toLowerCase().trim() ===
                                        c.name.toLowerCase().trim()),
                        );

                        let detail: ClientRecord | null = null;
                        if (!c.phone || !c.city || !c.addressLine1) {
                            try {
                                detail = await fetchCustomerById(c.id);
                            } catch {
                                // ignore
                            }
                        }

                        const totalSpentNum = clientOrders.reduce((sum, o) => {
                            const numeric =
                                typeof o.total === "string"
                                    ? parseFloat(
                                          o.total.replace(/[^0-9.-]+/g, ""),
                                      ) || 0
                                    : Number(o.total) || 0;
                            return sum + numeric;
                        }, 0);

                        const ordersCount = clientOrders.length;
                        const totalSpent =
                            clientOrders.length > 0
                                ? `€${totalSpentNum.toFixed(2)}`
                                : detail?.totalSpent &&
                                    detail.totalSpent !== "€0.00"
                                  ? detail.totalSpent
                                  : c.totalSpent;

                        return {
                            ...c,
                            ...(detail || {}),
                            ordersCount,
                            totalSpent,
                            phone: detail?.phone || c.phone || "",
                            city: detail?.city || c.city || "",
                            province: detail?.province || c.province || "",
                            addressLine1:
                                detail?.addressLine1 || c.addressLine1 || "",
                        };
                    }),
                );

                setClients(enrichedClients);
            } catch (error) {
                console.error("[Clienti] Failed to load clients:", error);
                setClients([]);
                setClientsError(
                    "Impossibile caricare l'elenco dei clienti da TS Commerce.",
                );
                toast.error("Impossibile contattare il server", {
                    id: "clienti-load-error",
                    description: "Non riesco a caricare l'elenco dei clienti.",
                });
            } finally {
                setIsLoadingClients(false);
            }
        },
        [activeAgentTag],
    );

    useEffect(() => {
        void loadCustomers();
    }, [loadCustomers]);

    const handleCreateCustomer = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newFullName.trim() || !newEmail.trim()) {
            toast.error("Nome e Email sono obbligatori");
            return;
        }

        setIsSubmittingCustomer(true);
        try {
            const userEnteredTags = newTags
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean);

            // Auto-assign the active agent tag (e.g. agente-707)
            if (
                agentTag &&
                !userEnteredTags.some(
                    (t) => t.toLowerCase() === agentTag.toLowerCase(),
                )
            ) {
                userEnteredTags.push(agentTag);
            }
            if (agentCode) {
                const standardTag = `agente-${agentCode}`;
                if (
                    !userEnteredTags.some(
                        (t) => t.toLowerCase() === standardTag.toLowerCase(),
                    )
                ) {
                    userEnteredTags.push(standardTag);
                }
            }

            const result = await submitCustomer({
                fullName: newFullName.trim(),
                email: newEmail.trim(),
                phone: newPhone.trim() || undefined,
                address: newAddress.trim() || undefined,
                city: newCity.trim() || undefined,
                state: newCountry.trim() || undefined,
                zip: newZip.trim() || undefined,
                province: newProvince.trim() || undefined,
                taxNumber: newTaxNumber.trim() || newCode.trim() || undefined,
                code: newCode.trim() || newTaxNumber.trim() || undefined,
                note: newNotes.trim() || undefined,
                notes: newNotes.trim() || undefined,
                tags: userEnteredTags,
            });

            if (result?.client?.id) {
                toast.success(
                    "Cliente registrato con successo nel sistema B2B",
                );
                setIsCreateModalOpen(false);
                // Clear fields
                setNewFullName("");
                setNewEmail("");
                setNewPhone("");
                setNewAddress("");
                setNewCity("");
                setNewZip("");
                setNewProvince("");
                setNewTaxNumber("");
                setNewNotes("");
                setNewCode("");
                setNewCountry("");
                setNewTags("");
                // Reload customers list
                void loadCustomers(true);
            }
        } catch (err) {
            console.error("[Clienti] Create customer failed:", err);
            toast.error("Impossibile registrare il cliente", {
                description: "Errore durante il collegamento con il server.",
            });
        } finally {
            setIsSubmittingCustomer(false);
        }
    };

    // -------------------------------------
    // Component functions
    // -------------------------------------

    function handleDeleteClient(clientId: string, clientName: string) {
        toast.warning(`Eliminare il cliente "${clientName}"?`, {
            id: `delete-client-${clientId}`,
            description:
                "Questa azione eliminerà permanentemente il cliente dal database.",
            duration: 10000,
            action: {
                label: "Elimina",
                onClick: async () => {
                    try {
                        await deleteCustomer(clientId);
                        toast.success(
                            `Cliente "${clientName}" eliminato con successo`,
                        );
                        void loadCustomers(true);
                    } catch (err) {
                        console.error(
                            "[Clienti] Failed to delete customer:",
                            err,
                        );
                        toast.error("Impossibile eliminare il cliente");
                    }
                },
            },
            cancel: {
                label: "Annulla",
                onClick: () => {},
            },
        });
    }

    function renderClientActionItems(client: ClientRecord) {
        return [
            <DropdownMenuItem key="order-history" asChild>
                <Link to={`/ordini?clientId=${client.id}`}>
                    <History className="w-4 h-4" />
                    Storico ordini
                </Link>
            </DropdownMenuItem>,
            <DropdownMenuItem
                key="delete-client"
                className="text-red-600 cursor-pointer"
                onClick={() => handleDeleteClient(client.id, client.name)}>
                <Trash2 className="w-4 h-4" />
                Elimina cliente
            </DropdownMenuItem>,
        ];
    }

    function handleResetFilters() {
        setSearchQuery("");
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    return (
        <DashboardLayout className={className}>
            <div className="w-full">
                <PageBreadcrumb
                    items={[{ label: "Home", to: "/" }, { label: "Clienti" }]}
                />

                <div className="border-b border-gray-200 mb-6 w-full" />

                {/* Main Card */}
                <Card className="shadow-sm border-gray-100">
                    <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-6">
                        <div>
                            <CardTitle className="text-2xl font-bold mb-1">
                                Clienti
                            </CardTitle>
                            <CardDescription className="text-sm text-gray-500">
                                Gestisci l'anagrafica dei tuoi clienti
                            </CardDescription>
                        </div>
                        <div className="flex items-center gap-3">
                            <Button
                                className="bg-[#0D6EFD] hover:bg-blue-700 text-white h-9 px-6 font-medium"
                                onClick={() => setIsCreateModalOpen(true)}>
                                <UserPlus className="mr-2 h-4 w-4" />
                                Nuovo cliente
                            </Button>
                        </div>
                    </CardHeader>

                    <CardContent className="space-y-4">
                        {deletedClientName && (
                            <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900 flex justify-between items-center shadow-sm">
                                <span>
                                    Cliente <strong>{deletedClientName}</strong>{" "}
                                    eliminato con successo dal database.
                                </span>
                                <button
                                    onClick={() =>
                                        navigate(location.pathname, {
                                            replace: true,
                                            state: {},
                                        })
                                    }
                                    className="text-green-700 hover:text-green-900 ml-3"
                                    aria-label="Chiudi avviso">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        )}

                        {clientsError && (
                            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                                {clientsError}
                            </div>
                        )}

                        {/* Search Bar */}
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <div className="relative w-full sm:w-[360px]">
                                <Input
                                    className="w-full pr-9 bg-white border-gray-200 text-gray-900 h-9 placeholder:text-gray-400"
                                    placeholder="Cerca cliente per nome, email o città..."
                                    value={searchQuery}
                                    onChange={(event) =>
                                        setSearchQuery(event.target.value)
                                    }
                                />
                                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            </div>
                        </div>

                        {isLoadingClients && (
                            <TableSkeleton
                                columns={6}
                                rows={pageSize}
                                mobileCards={3}
                            />
                        )}

                        {!isLoadingClients && (
                            <div className="space-y-3 lg:hidden">
                                {paginatedClients.map((client) => (
                                    <Card
                                        key={client.id}
                                        onClick={() =>
                                            navigate(`/clienti/${client.id}`)
                                        }
                                        className="shadow-sm border-gray-100 hover:bg-gray-50/75 cursor-pointer transition-colors">
                                        <CardContent className="p-4">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <div className="text-base font-semibold text-gray-900 truncate">
                                                            {client.name}
                                                        </div>
                                                        {client.city && (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                                                                {client.city}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-sm text-gray-500 truncate mt-1">
                                                        {client.email}
                                                    </div>
                                                    <div className="text-sm text-gray-500 truncate">
                                                        {client.phone}
                                                    </div>
                                                    <div className="flex items-center gap-4 mt-2 text-sm">
                                                        <div className="text-gray-900">
                                                            <span className="text-gray-500">
                                                                Ordini{" "}
                                                            </span>
                                                            <span className="font-medium">
                                                                {
                                                                    client.ordersCount
                                                                }
                                                            </span>
                                                        </div>
                                                        <div className="text-gray-900">
                                                            <span className="text-gray-500">
                                                                Spesa{" "}
                                                            </span>
                                                            <span className="font-medium">
                                                                {
                                                                    client.totalSpent
                                                                }
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div
                                                        className="flex flex-wrap gap-2 mt-4"
                                                        onClick={(e) =>
                                                            e.stopPropagation()
                                                        }>
                                                        <Button
                                                            asChild
                                                            variant="outline"
                                                            size="sm"
                                                            className="h-8 border-gray-200 text-gray-700">
                                                            <Link
                                                                to={`/clienti/${client.id}`}>
                                                                <Eye className="w-4 h-4" />
                                                                Vedi profilo
                                                            </Link>
                                                        </Button>
                                                        <Button
                                                            asChild
                                                            variant="outline"
                                                            size="sm"
                                                            className="h-8 border-gray-200 text-gray-700">
                                                            <Link
                                                                to={`/ordini?clientId=${client.id}`}>
                                                                <History className="w-4 h-4" />
                                                                Storico ordini
                                                            </Link>
                                                        </Button>
                                                    </div>
                                                </div>
                                                <div
                                                    onClick={(e) =>
                                                        e.stopPropagation()
                                                    }>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger
                                                            asChild>
                                                            <Button
                                                                variant="ghost"
                                                                className="h-8 w-8 p-0">
                                                                <span className="sr-only">
                                                                    Apri menu
                                                                </span>
                                                                <MoreHorizontal className="h-4 w-4 text-gray-500" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            {renderClientActionItems(
                                                                client,
                                                            )}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                                {filteredClients.length === 0 && (
                                    <EmptyState
                                        icon={UsersRound}
                                        title="Nessun cliente trovato"
                                        description="Nessun cliente corrisponde ai criteri di ricerca. Prova con nome, email o città diversi."
                                        actionLabel="Pulisci filtri"
                                        onAction={handleResetFilters}
                                        iconClassName="bg-blue-50 text-blue-600"
                                    />
                                )}
                            </div>
                        )}

                        {/* Table */}
                        {!isLoadingClients && (
                            <div className="hidden lg:block">
                                {filteredClients.length === 0 ? (
                                    <EmptyState
                                        icon={UsersRound}
                                        title="Nessun cliente trovato"
                                        description="Nessun cliente corrisponde ai criteri di ricerca. Prova con nome, email o città diversi."
                                        actionLabel="Pulisci filtri"
                                        onAction={handleResetFilters}
                                        iconClassName="bg-blue-50 text-blue-600"
                                    />
                                ) : (
                                    <div className="rounded-md border border-gray-100 overflow-hidden">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="hover:bg-transparent border-gray-100">
                                                    <TableHead className="font-medium text-gray-900">
                                                        <div className="flex items-center gap-1 cursor-pointer">
                                                            Nome Cliente
                                                            <ArrowUpDown className="w-3 h-3 text-gray-400" />
                                                        </div>
                                                    </TableHead>
                                                    <TableHead className="font-medium text-gray-900">
                                                        <div className="flex items-center gap-1 cursor-pointer">
                                                            Email
                                                            <ArrowUpDown className="w-3 h-3 text-gray-400" />
                                                        </div>
                                                    </TableHead>
                                                    <TableHead className="font-medium text-gray-900 hidden md:table-cell">
                                                        Telefono
                                                    </TableHead>
                                                    <TableHead className="font-medium text-gray-900 text-center">
                                                        Ordini
                                                    </TableHead>
                                                    <TableHead className="font-medium text-gray-900 text-right">
                                                        Spesa Totale
                                                    </TableHead>
                                                    <TableHead className="font-medium text-gray-900 text-center">
                                                        Città
                                                    </TableHead>
                                                    <TableHead className="w-12"></TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {paginatedClients.map(
                                                    (client) => (
                                                        <TableRow
                                                            key={client.id}
                                                            onClick={() =>
                                                                navigate(
                                                                    `/clienti/${client.id}`,
                                                                )
                                                            }
                                                            className="border-gray-100 hover:bg-gray-50/75 cursor-pointer transition-colors">
                                                            <TableCell className="text-gray-900 font-medium">
                                                                {client.name}
                                                            </TableCell>
                                                            <TableCell className="text-gray-500">
                                                                {client.email}
                                                            </TableCell>
                                                            <TableCell className="text-gray-500 hidden md:table-cell">
                                                                {client.phone}
                                                            </TableCell>
                                                            <TableCell className="text-gray-900 text-center">
                                                                {
                                                                    client.ordersCount
                                                                }
                                                            </TableCell>
                                                            <TableCell className="text-gray-900 font-medium text-right">
                                                                {
                                                                    client.totalSpent
                                                                }
                                                            </TableCell>
                                                            <TableCell className="text-center text-sm text-gray-600">
                                                                {client.city
                                                                    ? `${client.city}${client.province ? ` (${client.province})` : ""}`
                                                                    : "—"}
                                                            </TableCell>
                                                            <TableCell
                                                                onClick={(e) =>
                                                                    e.stopPropagation()
                                                                }>
                                                                <DropdownMenu>
                                                                    <DropdownMenuTrigger
                                                                        asChild>
                                                                        <Button
                                                                            variant="ghost"
                                                                            className="h-8 w-8 p-0">
                                                                            <span className="sr-only">
                                                                                Apri
                                                                                menu
                                                                            </span>
                                                                            <MoreHorizontal className="h-4 w-4 text-gray-500" />
                                                                        </Button>
                                                                    </DropdownMenuTrigger>
                                                                    <DropdownMenuContent align="end">
                                                                        {renderClientActionItems(
                                                                            client,
                                                                        )}
                                                                    </DropdownMenuContent>
                                                                </DropdownMenu>
                                                            </TableCell>
                                                        </TableRow>
                                                    ),
                                                )}
                                            </TableBody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Pagination Footer */}
                        <div className="flex flex-col sm:flex-row items-center justify-between pt-2 text-sm text-gray-500 gap-4">
                            <div>
                                Totale: {filteredClients.length}{" "}
                                {filteredClients.length === 1
                                    ? "cliente"
                                    : "clienti"}
                            </div>
                            <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-4 sm:gap-6">
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-900">
                                        Righe per page
                                    </span>
                                    <Select
                                        value={String(pageSize)}
                                        onValueChange={(val) => {
                                            setPageSize(Number(val));
                                            setCurrentPage(1);
                                        }}>
                                        <SelectTrigger className="h-8 w-16 bg-white border-gray-200">
                                            <SelectValue
                                                placeholder={String(pageSize)}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="10">
                                                10
                                            </SelectItem>
                                            <SelectItem value="20">
                                                20
                                            </SelectItem>
                                            <SelectItem value="50">
                                                50
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="font-medium text-gray-900">
                                    Page {currentPage} of {totalPages}
                                </div>

                                <div className="flex items-center gap-1">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-8 w-8 border-gray-200"
                                        disabled={currentPage <= 1}
                                        onClick={() => setCurrentPage(1)}>
                                        <ChevronsLeft className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-8 w-8 border-gray-200"
                                        disabled={currentPage <= 1}
                                        onClick={() =>
                                            setCurrentPage((p) =>
                                                Math.max(1, p - 1),
                                            )
                                        }>
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-8 w-8 border-gray-200"
                                        disabled={currentPage >= totalPages}
                                        onClick={() =>
                                            setCurrentPage((p) =>
                                                Math.min(totalPages, p + 1),
                                            )
                                        }>
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-8 w-8 border-gray-200"
                                        disabled={currentPage >= totalPages}
                                        onClick={() =>
                                            setCurrentPage(totalPages)
                                        }>
                                        <ChevronsRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Dialog
                open={isCreateModalOpen}
                onOpenChange={setIsCreateModalOpen}>
                <DialogContent className="sm:max-w-[480px] bg-white text-gray-900">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold text-gray-900">
                            Aggiungi nuovo cliente
                        </DialogTitle>
                        <DialogDescription className="text-gray-500 text-sm">
                            Inserisci le informazioni per registrare un nuovo
                            cliente B2B nel sistema.
                        </DialogDescription>
                        {(agentTag || agentCode) && (
                            <div className="pt-1">
                                <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded bg-[#E9E21B]/30 text-gray-900 border border-[#E9E21B]">
                                    Assegnazione Agente:{" "}
                                    {agentTag || `agente-${agentCode}`}
                                </span>
                            </div>
                        )}
                    </DialogHeader>

                    <form
                        onSubmit={handleCreateCustomer}
                        className="space-y-4 py-2">
                        <div className="space-y-1">
                            <Label
                                htmlFor="newFullName"
                                className="text-xs font-semibold text-gray-700">
                                Nome completo / Ragione Sociale{" "}
                                <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                id="newFullName"
                                required
                                value={newFullName}
                                onChange={(e) => setNewFullName(e.target.value)}
                                placeholder="es. Mario Rossi S.r.l."
                                className="bg-white border-gray-200 h-9"
                            />
                        </div>

                        <div className="space-y-1">
                            <Label
                                htmlFor="newEmail"
                                className="text-xs font-semibold text-gray-700">
                                Email <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                id="newEmail"
                                type="email"
                                required
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                placeholder="es. info@azienda.it"
                                className="bg-white border-gray-200 h-9"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <Label
                                    htmlFor="newPhone"
                                    className="text-xs font-semibold text-gray-700">
                                    Telefono
                                </Label>
                                <Input
                                    id="newPhone"
                                    value={newPhone}
                                    onChange={(e) =>
                                        setNewPhone(e.target.value)
                                    }
                                    placeholder="es. +39 02 123456"
                                    className="bg-white border-gray-200 h-9"
                                />
                            </div>

                            <div className="space-y-1">
                                <Label
                                    htmlFor="newAddress"
                                    className="text-xs font-semibold text-gray-700">
                                    Indirizzo
                                </Label>
                                <Input
                                    id="newAddress"
                                    value={newAddress}
                                    onChange={(e) =>
                                        setNewAddress(e.target.value)
                                    }
                                    placeholder="es. Via Roma 10"
                                    className="bg-white border-gray-200 h-9"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <Label
                                    htmlFor="newCity"
                                    className="text-xs font-semibold text-gray-700">
                                    Città
                                </Label>
                                <Input
                                    id="newCity"
                                    value={newCity}
                                    onChange={(e) => setNewCity(e.target.value)}
                                    placeholder="Milano"
                                    className="bg-white border-gray-200 h-9"
                                />
                            </div>

                            <div className="space-y-1">
                                <Label
                                    htmlFor="newProvince"
                                    className="text-xs font-semibold text-gray-700">
                                    Provincia (State)
                                </Label>
                                <Select
                                    name="state"
                                    value={newProvince}
                                    onValueChange={(val) =>
                                        setNewProvince(val)
                                    }>
                                    <SelectTrigger
                                        id="newProvince"
                                        className="bg-white border-gray-200 h-9">
                                        <SelectValue placeholder="Seleziona provincia" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-60">
                                        <SelectItem value="BL">
                                            Belluno (BL)
                                        </SelectItem>
                                        <SelectItem value="TV">
                                            Treviso (TV)
                                        </SelectItem>
                                        <SelectItem value="PD">
                                            Padova (PD)
                                        </SelectItem>
                                        <SelectItem value="VE">
                                            Venezia (VE)
                                        </SelectItem>
                                        <SelectItem value="VI">
                                            Vicenza (VI)
                                        </SelectItem>
                                        <SelectItem value="VR">
                                            Verona (VR)
                                        </SelectItem>
                                        <SelectItem value="RO">
                                            Rovigo (RO)
                                        </SelectItem>
                                        <SelectItem value="MI">
                                            Milano (MI)
                                        </SelectItem>
                                        <SelectItem value="RM">
                                            Roma (RM)
                                        </SelectItem>
                                        <SelectItem value="TO">
                                            Torino (TO)
                                        </SelectItem>
                                        <SelectItem value="BO">
                                            Bologna (BO)
                                        </SelectItem>
                                        <SelectItem value="FI">
                                            Firenze (FI)
                                        </SelectItem>
                                        <SelectItem value="NA">
                                            Napoli (NA)
                                        </SelectItem>
                                        <SelectItem value="BA">
                                            Bari (BA)
                                        </SelectItem>
                                        <SelectItem value="PA">
                                            Palermo (PA)
                                        </SelectItem>
                                        <SelectItem value="CT">
                                            Catania (CT)
                                        </SelectItem>
                                        <SelectItem value="TN">
                                            Trento (TN)
                                        </SelectItem>
                                        <SelectItem value="BZ">
                                            Bolzano (BZ)
                                        </SelectItem>
                                        <SelectItem value="UD">
                                            Udine (UD)
                                        </SelectItem>
                                        <SelectItem value="PN">
                                            Pordenone (PN)
                                        </SelectItem>
                                        <SelectItem value="TS">
                                            Trieste (TS)
                                        </SelectItem>
                                        <SelectItem value="GO">
                                            Gorizia (GO)
                                        </SelectItem>
                                        <SelectItem value="BS">
                                            Brescia (BS)
                                        </SelectItem>
                                        <SelectItem value="BG">
                                            Bergamo (BG)
                                        </SelectItem>
                                        <SelectItem value="CO">
                                            Como (CO)
                                        </SelectItem>
                                        <SelectItem value="VA">
                                            Varese (VA)
                                        </SelectItem>
                                        <SelectItem value="MN">
                                            Mantova (MN)
                                        </SelectItem>
                                        <SelectItem value="MO">
                                            Modena (MO)
                                        </SelectItem>
                                        <SelectItem value="PR">
                                            Parma (PR)
                                        </SelectItem>
                                        <SelectItem value="RE">
                                            Reggio Emilia (RE)
                                        </SelectItem>
                                        <SelectItem value="RA">
                                            Ravenna (RA)
                                        </SelectItem>
                                        <SelectItem value="FC">
                                            Forlì-Cesena (FC)
                                        </SelectItem>
                                        <SelectItem value="RN">
                                            Rimini (RN)
                                        </SelectItem>
                                        <SelectItem value="AN">
                                            Ancona (AN)
                                        </SelectItem>
                                        <SelectItem value="PU">
                                            Pesaro-Urbino (PU)
                                        </SelectItem>
                                        <SelectItem value="PG">
                                            Perugia (PG)
                                        </SelectItem>
                                        <SelectItem value="TR">
                                            Terni (TR)
                                        </SelectItem>
                                        <SelectItem value="PI">
                                            Pisa (PI)
                                        </SelectItem>
                                        <SelectItem value="LU">
                                            Lucca (LU)
                                        </SelectItem>
                                        <SelectItem value="AR">
                                            Arezzo (AR)
                                        </SelectItem>
                                        <SelectItem value="SI">
                                            Siena (SI)
                                        </SelectItem>
                                        <SelectItem value="GE">
                                            Genova (GE)
                                        </SelectItem>
                                        <SelectItem value="SV">
                                            Savona (SV)
                                        </SelectItem>
                                        <SelectItem value="SP">
                                            La Spezia (SP)
                                        </SelectItem>
                                        <SelectItem value="IM">
                                            Imperia (IM)
                                        </SelectItem>
                                        <SelectItem value="CA">
                                            Cagliari (CA)
                                        </SelectItem>
                                        <SelectItem value="SS">
                                            Sassari (SS)
                                        </SelectItem>
                                        <SelectItem value="EE">
                                            Estero (EE)
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <Label
                                    htmlFor="newZip"
                                    className="text-xs font-semibold text-gray-700">
                                    CAP
                                </Label>
                                <Input
                                    id="newZip"
                                    value={newZip}
                                    onChange={(e) => setNewZip(e.target.value)}
                                    placeholder="es. 32038"
                                    className="bg-white border-gray-200 h-9"
                                />
                            </div>

                            <div className="space-y-1">
                                <Label
                                    htmlFor="newCountry"
                                    className="text-xs font-semibold text-gray-700">
                                    Stato / Nazione (Country)
                                </Label>
                                <Select
                                    name="country"
                                    value={newCountry || "Italia"}
                                    onValueChange={(val) => setNewCountry(val)}>
                                    <SelectTrigger
                                        id="newCountry"
                                        className="bg-white border-gray-200 h-9">
                                        <SelectValue placeholder="Seleziona stato" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Italia">
                                            Italia
                                        </SelectItem>
                                        <SelectItem value="San Marino">
                                            San Marino
                                        </SelectItem>
                                        <SelectItem value="Svizzera">
                                            Svizzera
                                        </SelectItem>
                                        <SelectItem value="Francia">
                                            Francia
                                        </SelectItem>
                                        <SelectItem value="Germania">
                                            Germania
                                        </SelectItem>
                                        <SelectItem value="Spagna">
                                            Spagna
                                        </SelectItem>
                                        <SelectItem value="Austria">
                                            Austria
                                        </SelectItem>
                                        <SelectItem value="Regno Unito">
                                            Regno Unito
                                        </SelectItem>
                                        <SelectItem value="Altro">
                                            Altro
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <Label
                                htmlFor="newTaxNumber"
                                className="text-xs font-semibold text-gray-700">
                                P.IVA / Codice Fiscale
                            </Label>
                            <Input
                                id="newTaxNumber"
                                value={newTaxNumber}
                                onChange={(e) =>
                                    setNewTaxNumber(e.target.value)
                                }
                                placeholder="es. 05323420280"
                                className="bg-white border-gray-200 h-9"
                            />
                        </div>

                        <div className="space-y-1">
                            <Label
                                htmlFor="newNotes"
                                className="text-xs font-semibold text-gray-700">
                                Note B2B
                            </Label>
                            <textarea
                                id="newNotes"
                                rows={2}
                                value={newNotes}
                                onChange={(e) => setNewNotes(e.target.value)}
                                placeholder="Eventuali note sul cliente..."
                                className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </div>

                        <div className="space-y-1">
                            <Label
                                htmlFor="newTags"
                                className="text-xs font-semibold text-gray-700">
                                Tag / Etichette (separati da virgola)
                            </Label>
                            <Input
                                id="newTags"
                                value={newTags}
                                onChange={(e) => setNewTags(e.target.value)}
                                placeholder="es. VIP, Partner, Ingrosso"
                                className="bg-white border-gray-200 h-9"
                            />
                        </div>

                        <DialogFooter className="pt-4 border-t border-gray-100 flex gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setIsCreateModalOpen(false)}
                                className="h-9 border-gray-200 text-gray-700"
                                disabled={isSubmittingCustomer}>
                                Annulla
                            </Button>
                            <Button
                                type="submit"
                                className="h-9 bg-[#0D6EFD] hover:bg-blue-700 text-white font-medium"
                                disabled={isSubmittingCustomer}>
                                {isSubmittingCustomer
                                    ? "Salvataggio..."
                                    : "Salva cliente"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
